// hooks/useRedirectHandler.ts
import { navigate } from "@reach/router";
import { useCallback, useEffect } from "react";
import { useLocation } from "react-use";

import type { RedirectConfig } from "../types/RedirectConfig.type";
import {
	useCurrentCompany,
	useCurrentProfileType,
	useCurrentUser,
} from "hooks/auth";
import { useReferralToken } from "./useReferralToken";
import { useAuthContext } from "../context/AuthContextProvider";
import { useAppDispatch } from "newui/store/hooks";
import { setCurrentProfileType } from "../authSlice";
import { CompanyProfileType } from "../types/CompanyProfileType";
import { LOCAL_STORAGE_KEYS } from "newui/constants/localStorageKeys";
import { ReferralTokenType } from "../components/Login";

export const REDIRECT_LOGIN_PATHS = [
	"/login",
	"/member/login",
	"/verify-email",
	"/",
	"/verification",
	"/guest/signup",
	"/two-factor",
	"/two-factor-setup",
	"/reset-password-form",
	"/reset-password",
];

export const useRedirectHandler = ({
	isVerified,
	role,
	companyId,
}: RedirectConfig): void => {
	const location = useLocation();

	const dispatch = useAppDispatch();
	const { referralToken } = useReferralToken();

	const redirectUrl = localStorage.getItem(LOCAL_STORAGE_KEYS.REDIRECT_PATH);
	const redirectProfile = localStorage.getItem(
		LOCAL_STORAGE_KEYS.REDIRECT_PROFILE
	);
	const { currentUser, isGuest } = useCurrentUser();
	const currentCompany = useCurrentCompany();
	const isRfxFeatureEnabled = currentCompany?.enable_rfx_functionality;
	const { currentUserHomePage } = useAuthContext();
	const currentProfile = useCurrentProfileType();

	const clearRedirect = () => {
		localStorage.removeItem(LOCAL_STORAGE_KEYS.REDIRECT_PATH);
	};

	const navigateToRedirect = useCallback(() => {
		clearRedirect();
		navigate(redirectUrl);
	}, [redirectUrl]);

	// Save redirect params from URL
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const redirect = urlParams.get("redirect");
		const page = urlParams.get("page");

		if (redirect) {
			localStorage.setItem(LOCAL_STORAGE_KEYS.REDIRECT_PATH, redirect);
		}

		if (page) {
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.REDIRECT_PROFILE,
				page.charAt(0).toUpperCase() + page.slice(1)
			);
		}

		if (redirectUrl === location.origin + location.pathname) {
			clearRedirect();
		}
	}, [location.pathname, location.origin, redirectUrl, redirectProfile]);

	// handle guest redirects
	useEffect(() => {
		if (!isGuest) {
			return;
		}

		const url_path = window.location.pathname;

		if (REDIRECT_LOGIN_PATHS.includes(url_path)) {
			if (redirectUrl) {
				navigateToRedirect();
			} else {
				navigate(currentUserHomePage);
			}
			return;
		}

		if (redirectUrl) {
			navigateToRedirect();
			return;
		}
	}, [isGuest, currentUserHomePage, redirectUrl, navigateToRedirect]);

	// Handle non-guest redirects after login
	useEffect(() => {
		if (isGuest || !currentUser) {
			return;
		}

		const url_path = window.location.pathname;

		// Shared token redirect
		if (
			referralToken &&
			referralToken.tokenType === ReferralTokenType.SharedProfile &&
			currentUser
		) {
			navigate(`/share-profile?sc=${referralToken.tokenValue}`);
			return;
		}

		// Apply redirect profile switch for email links
		if (redirectProfile) {
			const profileMap: Record<string, CompanyProfileType> = {
				Bidder: CompanyProfileType.SUPPLIER,
				Procurer: CompanyProfileType.CLIENT,
			};

			if (profileMap[redirectProfile]) {
				dispatch(setCurrentProfileType(profileMap[redirectProfile]));
			}
			localStorage.removeItem(LOCAL_STORAGE_KEYS.REDIRECT_PROFILE);
		}

		// Verified user redirects
		if (isVerified && REDIRECT_LOGIN_PATHS.includes(url_path) && companyId) {
			if (redirectUrl) {
				navigateToRedirect();
				return;
			}

			navigate(currentUserHomePage);
		}

		// Simple redirect if URL is set
		if (redirectUrl && !REDIRECT_LOGIN_PATHS.includes(url_path)) {
			navigateToRedirect();
		}
	}, [
		isVerified,
		redirectUrl,
		isGuest,
		role,
		companyId,
		isRfxFeatureEnabled,
		currentUserHomePage,
		currentProfile,
		currentUser,
		referralToken,
		navigateToRedirect,
		dispatch,
		redirectProfile,
	]);
};
